import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import IconPrevArrow from '../IconPrevArrow/IconPrevArrow';
import IconNextArrow from '../IconNextArrow/IconNextArrow';
import { isArrayLength } from '../../util/dataExtractors';
import css from './ImageSlider.module.css';

const DEFAULT_FADE_DURATION_MS = 280;
const SWIPE_THRESHOLD_PX = 40;

const getImageConfig = image => {
  if (typeof image === 'string') {
    return { src: image };
  }

  if (image && typeof image === 'object') {
    return image;
  }

  return {};
};

function ImageSlider(props) {
  const {
    images,
    isMobile,
    pushToListingPage,
    isSectionHero = false,
    imgAlt = '',
    autoplayIntervalMs = 2500,
    fadeDurationMs = DEFAULT_FADE_DURATION_MS,
    onActiveIndexChange,
  } = props;

  const handleDragStart = e => e.preventDefault();

  const location = useLocation();
  const safeImages = isArrayLength(images) ? images : [];
  const hasMultipleImages = safeImages.length > 1;
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  // The hero keeps the displayed slide until its replacement has loaded.
  const [pendingIndex, setPendingIndex] = useState(null);
  const [heroImageStatus, setHeroImageStatus] = useState({});
  const activeIndexRef = useRef(0);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);

  const imageClassName = classNames({
    [css.images]: !isSectionHero,
    [css.heroImages]: isSectionHero,
  });
  const carouselClassName = classNames(css.carouselContainer, {
    [css.sectionHeroContainer]: isSectionHero,
  });
  const cardSliderClassName = classNames(css.cardSlider, {
    [css.sectionHeroSlider]: isSectionHero,
  });
  const imageViewportClassName = classNames(css.imageViewport, {
    [css.sectionHeroViewport]: isSectionHero,
    [css.heroPlaceholder]: isSectionHero,
  });

  const isLandingPage = location && location.pathname === '/';

  // State to keep track of whether component is mounted
  const [isMounted, setIsMounted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Cancel subscriptions and asynchronous tasks when component is unmounted
  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    if (typeof onActiveIndexChange === 'function') {
      onActiveIndexChange(activeIndex);
    }
  }, [activeIndex, onActiveIndexChange]);

  useEffect(() => {
    if (!safeImages.length) {
      setActiveIndex(0);
      setPreviousIndex(null);
      activeIndexRef.current = 0;
      return;
    }

    if (activeIndexRef.current >= safeImages.length) {
      setActiveIndex(0);
      setPreviousIndex(null);
      activeIndexRef.current = 0;
    }
  }, [safeImages.length]);

  useEffect(() => {
    if (previousIndex === null) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setPreviousIndex(null);
    }, fadeDurationMs);

    return () => clearTimeout(timer);
  }, [previousIndex, activeIndex, fadeDurationMs]);

  const updateActiveIndex = useCallback(
    nextIndex => {
      if (!safeImages.length) {
        return;
      }

      const normalizedIndex =
        ((nextIndex % safeImages.length) + safeImages.length) % safeImages.length;
      const currentIndex = activeIndexRef.current;

      if (normalizedIndex === currentIndex) {
        return;
      }

      if (isSectionHero) {
        setPendingIndex(normalizedIndex);
        return;
      }

      setPreviousIndex(currentIndex);
      setActiveIndex(normalizedIndex);
      activeIndexRef.current = normalizedIndex;
    },
    [safeImages.length, isSectionHero]
  );

  const activeHeroLoaded =
    heroImageStatus[getImageConfig(safeImages[activeIndex]).src] === 'loaded';

  useEffect(() => {
    if (!isMounted || !hasMultipleImages) {
      return undefined;
    }
    const autoplay = isSectionHero || (isMobile && isLandingPage);
    if (!autoplay) {
      return undefined;
    }
    if (isSectionHero && (!activeHeroLoaded || pendingIndex !== null)) {
      return undefined;
    }
    const timer = setInterval(() => {
      const nextIndex = isSectionHero
        ? safeImages.findIndex((_, offset) => {
            const index = (activeIndexRef.current + offset + 1) % safeImages.length;
            return heroImageStatus[getImageConfig(safeImages[index]).src] !== 'error';
          })
        : 0;
      if (nextIndex >= 0) {
        updateActiveIndex(activeIndexRef.current + nextIndex + 1);
      }
    }, autoplayIntervalMs);
    return () => clearInterval(timer);
  }, [
    autoplayIntervalMs,
    isMounted,
    isSectionHero,
    isMobile,
    isLandingPage,
    hasMultipleImages,
    safeImages.length,
    updateActiveIndex,
    activeHeroLoaded,
    pendingIndex,
    heroImageStatus,
  ]);

  const goPrev = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!safeImages.length) return;
    updateActiveIndex(activeIndexRef.current - 1);
  };

  const goNext = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!safeImages.length) return;
    updateActiveIndex(activeIndexRef.current + 1);
  };

  const goToSlide = (e, index) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    updateActiveIndex(index);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleTouchStart = e => {
    if (!isMobile || !hasMultipleImages) {
      return;
    }

    const touch = e.touches && e.touches[0];
    if (!touch) {
      return;
    }

    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const resetTouchState = () => {
    touchStartXRef.current = null;
    touchStartYRef.current = null;
  };

  const handleTouchEnd = e => {
    if (!isMobile || !hasMultipleImages || touchStartXRef.current === null) {
      resetTouchState();
      return;
    }

    const touch = e.changedTouches && e.changedTouches[0];
    if (!touch) {
      resetTouchState();
      return;
    }

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - (touchStartYRef.current ?? touch.clientY);

    resetTouchState();

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX > 0) {
      goPrev(e);
    } else {
      goNext(e);
    }
  };

  const enableCrossFade = hasMultipleImages;
  const activeImage = getImageConfig(safeImages[activeIndex]);
  const previousImage =
    enableCrossFade && previousIndex !== null ? getImageConfig(safeImages[previousIndex]) : null;

  const handleHeroLoad = (index, src) => {
    setHeroImageStatus(status => ({ ...status, [src]: 'loaded' }));
    if (index === pendingIndex) {
      setPreviousIndex(activeHeroLoaded ? activeIndexRef.current : null);
      setActiveIndex(index);
      activeIndexRef.current = index;
      setPendingIndex(null);
    }
  };

  const handleHeroError = (index, src) => {
    const status = { ...heroImageStatus, [src]: 'error' };
    setHeroImageStatus(status);
    // A failed alternate must never replace a successfully displayed image.
    const nextOffset = safeImages.findIndex((_, offset) => {
      const candidate = (index + offset + 1) % safeImages.length;
      return status[getImageConfig(safeImages[candidate]).src] !== 'error';
    });
    const nextIndex = nextOffset < 0 ? null : (index + nextOffset + 1) % safeImages.length;
    setPendingIndex(nextIndex === activeIndex && activeHeroLoaded ? null : nextIndex);
  };

  const onImageClick = () => {
    if (typeof pushToListingPage === 'function') {
      pushToListingPage();
    }
  };

  return (
    <div
      className={carouselClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={resetTouchState}
      style={{ '--ImageSlider_fadeDuration': `${fadeDurationMs}ms` }}
    >
      {(isSectionHero || isMounted) && isArrayLength(safeImages) ? (
        <div className={cardSliderClassName}>
          <div className={imageViewportClassName}>
            {isSectionHero
              ? [...new Set([activeIndex, previousIndex, pendingIndex])]
                  .filter(index => index !== null)
                  .map(index => {
                    const image = getImageConfig(safeImages[index]);
                    if (!image.src) return null;
                    const pending = index === pendingIndex;
                    const failed = heroImageStatus[image.src] === 'error';
                    return (
                      <img
                        key={image.src}
                        src={image.src}
                        srcSet={image.srcSet}
                        sizes={image.sizes}
                        width={image.width}
                        height={image.height}
                        alt={image.alt || imgAlt}
                        onDragStart={handleDragStart}
                        role="presentation"
                        className={classNames(imageClassName, css.fadeImage, {
                          [css.activeImage]: index === activeIndex,
                          [css.previousImage]: index === previousIndex,
                          [css.fadeIn]: index === activeIndex && previousIndex !== null,
                          [css.pendingHeroImage]: pending || failed,
                        })}
                        style={image.style}
                        onClick={onImageClick}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        fetchpriority={index === 0 ? 'high' : 'low'}
                        onLoad={() => handleHeroLoad(index, image.src)}
                        onError={() => handleHeroError(index, image.src)}
                        ref={element => {
                          // SSR images can finish before hydration attaches onLoad.
                          if (element?.complete && (!heroImageStatus[image.src] || pending)) {
                            if (element.naturalWidth > 0) handleHeroLoad(index, image.src);
                            else handleHeroError(index, image.src);
                          }
                        }}
                      />
                    );
                  })
              : null}
            {!isSectionHero && previousImage?.src ? (
              <img
                key={`prev-${previousIndex}`}
                src={previousImage.src}
                alt={previousImage.alt || imgAlt}
                onDragStart={handleDragStart}
                role="presentation"
                className={classNames(imageClassName, css.fadeImage, css.previousImage)}
                style={previousImage.style}
                onClick={onImageClick}
                loading="lazy"
              />
            ) : null}
            {!isSectionHero && activeImage?.src ? (
              <img
                key={`active-${activeIndex}`}
                src={activeImage.src}
                alt={activeImage.alt || imgAlt}
                onDragStart={handleDragStart}
                role="presentation"
                className={classNames(
                  imageClassName,
                  enableCrossFade ? css.fadeImage : null,
                  css.activeImage,
                  enableCrossFade && previousImage ? css.fadeIn : null
                )}
                style={activeImage.style}
                onClick={onImageClick}
                loading={isSectionHero && activeIndex === 0 ? 'eager' : 'lazy'}
              />
            ) : null}
          </div>
          {!isMobile && isHovered && hasMultipleImages ? (
            <>
              <button
                className={classNames(css.manualArrow, css.prevArrow)}
                type="button"
                onClick={goPrev}
              >
                <IconPrevArrow className={css.icon} />
              </button>
              <button
                className={classNames(css.manualArrow, css.nextArrow)}
                type="button"
                onClick={goNext}
              >
                <IconNextArrow className={css.icon} />
              </button>
            </>
          ) : null}
          {!isSectionHero && hasMultipleImages ? (
            <div className={css.dots}>
              {safeImages.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={classNames(css.dot, index === activeIndex ? css.dotActive : null)}
                  onClick={e => goToSlide(e, index)}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className={css.placeholder} />
      )}
    </div>
  );
}

export default ImageSlider;
